import { useState, useEffect, useCallback } from 'react';
import { api, uploadUrl } from '../api';
import { showUndo } from '../components/UndoToast';
import { usePremium } from '../contexts/PremiumContext';
import { useWS } from '../ws';
import ProductGroup from '../components/ProductGroup';
import AddItemModal from '../components/AddItemModal';
import ItemDetailModal from '../components/ItemDetailModal';
import { useConfirm } from '../components/ConfirmDialog';
import { usePreferences } from '../contexts/PreferencesContext';
import { useTranslation } from 'react-i18next';

function hasDietConflict(group, filters) {
  if (!filters.length) return false;
  const first = group[0];
  const allergens = first.allergens || [];
  const traces    = first.traces    || [];
  const labels    = first.labels    || [];
  for (const f of filters) {
    if (f === 'gluten-free' && (allergens.includes('gluten') || traces.includes('gluten'))) return true;
    if (f === 'milk-free'   && (allergens.includes('milk')   || traces.includes('milk')))   return true;
    if (f === 'nut-free'    && (allergens.some(a => a === 'nuts' || a === 'peanuts') || traces.some(t => t === 'nuts' || t === 'peanuts'))) return true;
    if (f === 'lactose-free' && !labels.includes('lactose-free')) continue; // manglende label = ukendt, ingen konflikt — hop over uden at returnere
    if (f === 'vegetarian'  && labels.includes('non-vegetarian')) return true;
    if (f === 'vegan'       && labels.includes('non-vegan'))       return true;
  }
  return false;
}

const CONFIG = {
  freezer: { icon: '❄️', color: 'oklch(60% 0.17 230)', muted: 'oklch(60% 0.17 230 / 14%)' },
  fridge:  { icon: '🥛', color: 'oklch(62% 0.16 185)', muted: 'oklch(62% 0.16 185 / 14%)' },
  pantry:  { icon: '🥫', color: 'oklch(70% 0.17 72)',  muted: 'oklch(70% 0.17 72 / 14%)'  },
};

// ── Billede-kort til grid-visning ─────────────────────────────────────────
function ImageCard({ items, onTap, onUse, onOpen, accentColor }) {
  const { t } = useTranslation();
  const first = items[0];
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const openedQty = items.filter(i => i.opened).reduce((s, i) => s + i.quantity, 0);

  // Prioriter eget uploadet billede, fallback til OFF-thumbnail
  const imgSrc = first.images?.[0]?.filename
    ? uploadUrl(first.images[0].filename)
    : first.thumbnail || null;

  const minExpiry = items.reduce((min, i) => {
    if (!i.expiry_date) return min;
    return min === null || i.expiry_date < min ? i.expiry_date : min;
  }, null);
  const days = minExpiry ? Math.ceil((new Date(minExpiry) - new Date()) / 86400000) : null;
  const statusColor = days === null ? null
    : days < 0  ? 'var(--status-expired)'
    : days <= 3 ? 'var(--status-warning)'
    : 'var(--status-fresh)';

  // Åbn er kun relevant hvis qty > 1 (splitter én ud som åbnet)
  const canOpen = totalQty > 1 || !first.opened;

  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: 12,
      overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Billede-felt — tryk for detalje */}
      <button onClick={() => onTap(first)} style={{
        background: 'var(--bg-elevated)', height: 110,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', width: '100%', border: 'none', cursor: 'pointer', padding: 0,
      }}>
        {imgSrc
          ? <img src={imgSrc} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontSize: 38 }}>📦</span>}

        {/* Antal-badge */}
        <div style={{
          position: 'absolute', top: 7, right: 7,
          background: 'oklch(0% 0 0 / 55%)', borderRadius: 999,
          padding: '2px 9px', fontSize: 14, fontWeight: 700,
          color: '#fff', fontFamily: 'Manrope, sans-serif',
        }}>{totalQty}</div>

        {/* Åbne-badge */}
        {openedQty > 0 && (
          <div style={{
            position: 'absolute', top: 7, left: 7,
            background: 'oklch(64% 0.18 230 / 85%)', borderRadius: 999,
            padding: '2px 7px', fontSize: 10, fontWeight: 700,
            color: '#fff', fontFamily: 'Manrope, sans-serif',
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <span>📦</span><span>{openedQty}</span>
          </div>
        )}

        {/* Udløb-dot */}
        {statusColor && (
          <div style={{
            position: 'absolute', bottom: 7, left: 7,
            width: 9, height: 9, borderRadius: '50%',
            background: statusColor,
            boxShadow: '0 0 0 2px oklch(0% 0 0 / 30%)',
          }} />
        )}
      </button>

      {/* Navn + meta */}
      <button onClick={() => onTap(first)} style={{
        padding: '7px 10px 4px', border: 'none', background: 'transparent',
        cursor: 'pointer', textAlign: 'left', width: '100%',
      }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-1)', fontFamily: 'Manrope, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{first.name}</p>
        <p style={{ fontSize: 10, color: 'var(--fg-3)', fontFamily: 'Manrope, sans-serif', margin: '1px 0 0' }}>{t('units.' + (first.unit || 'stk'), first.unit || 'stk')}{first.location_name ? ` · ${first.location_name}` : ''}</p>
      </button>

      {/* Handlinger — stopper event-bobling */}
      <div style={{ display: 'flex', borderTop: '1px solid var(--border-subtle)', marginTop: 2 }}>
        {canOpen && (
          <button
            onClick={e => { e.stopPropagation(); onOpen(first); }}
            style={{
              flex: 1, padding: '7px 4px', border: 'none', borderRight: '1px solid var(--border-subtle)',
              background: 'transparent', cursor: 'pointer',
              fontSize: 10, fontWeight: 600, color: 'var(--fg-3)', fontFamily: 'Manrope, sans-serif',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            }}>
            <span style={{ fontSize: 14 }}>📦</span>
            <span>{t('storage.open')}</span>
          </button>
        )}
        <button
          onClick={e => { e.stopPropagation(); onUse(first); }}
          style={{
            flex: 1, padding: '7px 4px', border: 'none',
            background: 'transparent', cursor: 'pointer',
            fontSize: 10, fontWeight: 600, fontFamily: 'Manrope, sans-serif',
            color: accentColor,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
          }}>
          <span style={{ fontSize: 14 }}>−1</span>
          <span>{t('storage.use')}</span>
        </button>
      </div>
    </div>
  );
}

// ── View-toggle pill (Liste | Billeder) ───────────────────────────────────
function ViewToggle({ viewMode, setViewMode, isPremium }) {
  const { t } = useTranslation();
  const pill = (mode, label, locked) => (
    <button onClick={() => { if (!locked) setViewMode(mode); }} style={{
      border: 'none', cursor: locked ? 'not-allowed' : 'pointer',
      fontFamily: 'Manrope, sans-serif', fontSize: 11, fontWeight: 700,
      padding: '5px 12px', borderRadius: 999,
      background: viewMode === mode ? 'var(--bg-card)' : 'transparent',
      color: viewMode === mode ? 'var(--fg-1)' : locked ? 'var(--fg-4)' : 'var(--fg-3)',
      boxShadow: viewMode === mode ? 'var(--shadow-sm)' : 'none',
      display: 'flex', alignItems: 'center', gap: 4,
      opacity: locked ? 0.6 : 1,
      transition: 'all 150ms',
    }}>
      {label}{locked && <span style={{ fontSize: 8 }}>🔒</span>}
    </button>
  );
  return (
    <div style={{ background: 'var(--bg-sunken)', borderRadius: 999, padding: 3, display: 'flex' }}>
      {pill('list', `☰ ${t('storage.viewList')}`, false)}
      {pill('image', `▦ ${t('storage.viewImages')}`, !isPremium)}
    </div>
  );
}

export default function ListPage({ listType }) {
  const { t } = useTranslation();
  const cfg = CONFIG[listType];
  const { filters } = usePreferences();
  const [items, setItems]               = useState([]);
  const [locations, setLocations]       = useState([]);
  const [storageUnits, setStorageUnits] = useState([]);
  const [allStorageUnits, setAllStorageUnits] = useState([]);
  const [activeUnit, setActiveUnit]     = useState(null);
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [selected, setSelected]         = useState(null);
  const [search, setSearch]             = useState('');
  const [viewMode, setViewMode]         = useState(() => localStorage.getItem(`viewMode_${listType}`) || 'list');
  const { confirm, ConfirmDialog }      = useConfirm();
  const { isPremium }                   = usePremium();

  const load = useCallback(async () => {
    try {
      const [inv, locs, units, allUnits] = await Promise.all([
        api.getInventory(listType),
        api.getLocations(listType),
        api.getStorageUnits(listType),
        api.getStorageUnits(),
      ]);
      setItems(inv); setLocations(locs); setStorageUnits(units); setAllStorageUnits(allUnits);
    } finally {
      setLoading(false);
    }
  }, [listType]);

  useEffect(() => {
    setLoading(true);
    setActiveUnit(null);
    setViewMode(localStorage.getItem(`viewMode_${listType}`) || 'list');
    load();
  }, [load, listType]);

  const handleSetViewMode = (mode) => {
    localStorage.setItem(`viewMode_${listType}`, mode);
    setViewMode(mode);
  };
  useWS(useCallback((msg) => {
    if (msg.event === 'inventory:changed' && msg.data.list_type === listType) load();
    if (msg.event === 'storage_units:changed') load();
  }, [listType, load]));

  const useOne = async (item) => { await api.useInventory(item.id); load(); };

  const useAll = async (item) => {
    await api.deleteInventory(item.id);
    load();
    showUndo(`${item.name} brugt`, async () => {
      await api.addInventory({
        product_id: item.product_id, quantity: item.quantity, list_type: item.list_type,
        location_id: item.location_id, expiry_date: item.expiry_date,
        date_added: item.date_added, notes: item.notes,
        opened: item.opened, opened_at: item.opened_at,
      });
    });
  };

  const splitOpen = async (item) => {
    const today = new Date().toISOString().slice(0, 10);
    if (item.quantity <= 1) {
      // Kun 1 stk — marker direkte som åben uden at splitte (undgår 0-quantity-post)
      await api.updateInventory(item.id, {
        quantity: 1, location_id: item.location_id || null,
        expiry_date: item.expiry_date || null, date_added: item.date_added || null,
        notes: item.notes || null, opened: 1, opened_at: today,
      });
    } else {
      await api.updateInventory(item.id, { quantity: item.quantity - 1, location_id: item.location_id || null, expiry_date: item.expiry_date || null, date_added: item.date_added || null, notes: item.notes || null, opened: 0 });
      await api.addInventory({ product_id: item.product_id, quantity: 1, list_type: listType, location_id: item.location_id || null, expiry_date: item.expiry_date || null, date_added: item.date_added || null, notes: item.notes || null, opened: 1, opened_at: today });
    }
    load();
  };

  const deleteItem = async (item) => {
    const ok = await confirm(t('storage.deleteConfirm', { name: item.name }));
    if (!ok) return;
    await api.deleteInventory(item.id);
    load();
    showUndo(`${item.name} slettet`, async () => {
      await api.addInventory({ product_id: item.product_id, quantity: item.quantity, list_type: item.list_type, location_id: item.location_id, expiry_date: item.expiry_date, date_added: item.date_added, notes: item.notes, opened: item.opened, opened_at: item.opened_at });
    });
  };

  const filtered = items.filter(i => {
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeUnit) {
      const unitLocs = locations.filter(l => l.storage_unit_id === activeUnit);
      const unitLocIds = new Set(unitLocs.map(l => l.id));
      return i.location_id ? unitLocIds.has(i.location_id) : false;
    }
    return true;
  });

  const groupByProduct = (itemList) => {
    const map = new Map();
    for (const item of itemList) {
      if (!map.has(item.product_id)) map.set(item.product_id, []);
      map.get(item.product_id).push(item);
    }
    return Array.from(map.values());
  };

  // ── Billedgrid ────────────────────────────────────────────────────────
  const renderImageCard = (group, uniqueKey) => (
    <ImageCard
      key={uniqueKey ?? group[0].product_id}
      items={group}
      onTap={setSelected}
      onUse={useOne}
      onOpen={splitOpen}
      accentColor={cfg.color}
    />
  );

  const renderImageGrid = () => {
    if (filtered.length === 0) return renderEmpty();

    // Byg en flad liste af sektioner og kort til grid-rendering
    const sections = [];

    const addSection = (label, itemList, key) => {
      if (itemList.length === 0) return;
      sections.push({ type: 'header', label, key: `hdr-${key}` });
      // Key SKAL inkludere lokationen — samme produkt kan ligge i flere lokationer,
      // og rene product_id-keys kolliderer så React reconciliation efterlader stale kort
      groupByProduct(itemList).forEach(g => sections.push({ type: 'card', group: g, key: `${key}-${g[0].product_id}` }));
    };

    if (storageUnits.length <= 1) {
      const knownLocIds = new Set(locations.map(l => l.id));
      const noLoc = filtered.filter(i => !i.location_id || !knownLocIds.has(i.location_id));
      locations.forEach(loc => addSection(loc.name, filtered.filter(i => i.location_id === loc.id), loc.id));
      if (noLoc.length > 0) addSection(t('item.noLocation'), noLoc, 'noloc');
    } else {
      const unitsToShow = activeUnit ? storageUnits.filter(u => u.id === activeUnit) : storageUnits;
      const allUnitLocIds = new Set(locations.map(l => l.id));
      const noLocItems = filtered.filter(i => !i.location_id || !allUnitLocIds.has(i.location_id));
      const firstUnitId = unitsToShow[0]?.id;

      unitsToShow.forEach(unit => {
        const unitLocs = locations.filter(l => l.storage_unit_id === unit.id);
        const showNoLoc = unit.id === firstUnitId && noLocItems.length > 0 && !activeUnit;
        const hasItems = unitLocs.some(loc => filtered.some(i => i.location_id === loc.id)) || showNoLoc;
        if (!hasItems) return;

        if (!activeUnit && storageUnits.length > 1) {
          sections.push({ type: 'unit-header', unit, key: `unit-${unit.id}` });
        }
        unitLocs.forEach(loc => addSection(loc.name, filtered.filter(i => i.location_id === loc.id), loc.id));
        if (showNoLoc) addSection(t('item.noLocation'), noLocItems, 'noloc');
      });
    }

    if (sections.length === 0) {
      // Ingen lokationsdata — vis fladt grid
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, padding: '12px 16px' }}>
          {groupByProduct(filtered).map(g => renderImageCard(g))}
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, padding: '12px 16px' }}>
        {sections.map(s => {
          if (s.type === 'unit-header') return (
            <div key={s.key} style={{
              gridColumn: '1 / -1',
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--bg-sunken)',
              padding: '10px 16px 6px',
              marginLeft: -16, marginRight: -16,
              width: 'calc(100% + 32px)',
            }}>
              <span style={{ fontSize: 16 }}>{s.unit.icon || cfg.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-2)', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif' }}>{s.unit.name}</span>
            </div>
          );
          if (s.type === 'header') return (
            <div key={s.key} style={{ gridColumn: '1 / -1' }} className="ft-section-header">
              <span className="ft-section-label">{s.label}</span>
            </div>
          );
          return renderImageCard(s.group, s.key);
        })}
      </div>
    );
  };

  const renderEmpty = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 64, color: 'var(--fg-3)', fontFamily: 'Manrope, sans-serif', gap: 8 }}>
      <span style={{ fontSize: 48 }}>{cfg.icon}</span>
      <p style={{ fontSize: 15 }}>{search ? t('storage.noResults') : t('storage.empty', { list: t(`nav.${listType}`) })}</p>
      {!search && <button onClick={() => setShowAdd(true)} style={{ color: cfg.color, fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>{t('storage.addSomething')}</button>}
    </div>
  );

  // ── Listetilstand ─────────────────────────────────────────────────────
  const renderListItems = () => {
    if (storageUnits.length <= 1) {
      const byLocation = locations.map(loc => ({
        loc, items: filtered.filter(i => i.location_id === loc.id),
      })).filter(g => g.items.length > 0);
      const knownLocIds = new Set(locations.map(l => l.id));
      const noLoc = filtered.filter(i => !i.location_id || !knownLocIds.has(i.location_id));

      return (
        <>
          {byLocation.map(({ loc, items: locItems }) => (
            <section key={loc.id}>
              <div className="ft-section-header"><span className="ft-section-label">{loc.name}</span></div>
              <div style={{ background: 'var(--bg-card)' }}>
                {groupByProduct(locItems).map(group => (
                  <ProductGroup key={group[0].product_id} items={group} listType={listType}
                    accentColor={cfg.color} onTap={setSelected} onUse={useOne}
                    onDelete={useAll} onChanged={load} onSplitOpen={splitOpen}
                    dietWarning={hasDietConflict(group, filters)} />
                ))}
              </div>
            </section>
          ))}
          {noLoc.length > 0 && (
            <section>
              {byLocation.length > 0 && <div className="ft-section-header"><span className="ft-section-label">{t('item.noLocation')}</span></div>}
              <div style={{ background: 'var(--bg-card)' }}>
                {groupByProduct(noLoc).map(group => (
                  <ProductGroup key={group[0].product_id} items={group} listType={listType}
                    accentColor={cfg.color} onTap={setSelected} onUse={useOne}
                    onDelete={useAll} onChanged={load} onSplitOpen={splitOpen}
                    dietWarning={hasDietConflict(group, filters)} />
                ))}
              </div>
            </section>
          )}
        </>
      );
    }

    const unitsToShow = activeUnit
      ? storageUnits.filter(u => u.id === activeUnit)
      : storageUnits;

    const allUnitLocIds = new Set(locations.map(l => l.id));
    const noLocItems = filtered.filter(i => !i.location_id || !allUnitLocIds.has(i.location_id));
    const firstUnitId = unitsToShow[0]?.id;

    return unitsToShow.map((unit) => {
      const unitLocs = locations.filter(l => l.storage_unit_id === unit.id);
      const byLocation = unitLocs.map(loc => ({
        loc, items: filtered.filter(i => i.location_id === loc.id),
      })).filter(g => g.items.length > 0);
      const showNoLoc = unit.id === firstUnitId && noLocItems.length > 0 && !activeUnit;
      const hasAnything = byLocation.length > 0 || showNoLoc;
      if (!hasAnything && activeUnit) return null;

      return (
        <section key={unit.id}>
          {!activeUnit && storageUnits.length > 1 && (
            <div style={{ padding: '12px 16px 6px', background: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>{unit.icon || cfg.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-2)', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif' }}>{unit.name}</span>
            </div>
          )}
          {byLocation.map(({ loc, items: locItems }) => (
            <div key={loc.id}>
              <div className="ft-section-header"><span className="ft-section-label">{loc.name}</span></div>
              <div style={{ background: 'var(--bg-card)' }}>
                {groupByProduct(locItems).map(group => (
                  <ProductGroup key={group[0].product_id} items={group} listType={listType}
                    accentColor={cfg.color} onTap={setSelected} onUse={useOne}
                    onDelete={useAll} onChanged={load} onSplitOpen={splitOpen}
                    dietWarning={hasDietConflict(group, filters)} />
                ))}
              </div>
            </div>
          ))}
          {showNoLoc && (
            <div>
              <div className="ft-section-header"><span className="ft-section-label">{t('item.noLocation')}</span></div>
              <div style={{ background: 'var(--bg-card)' }}>
                {groupByProduct(noLocItems).map(group => (
                  <ProductGroup key={group[0].product_id} items={group} listType={listType}
                    accentColor={cfg.color} onTap={setSelected} onUse={useOne}
                    onDelete={useAll} onChanged={load} onSplitOpen={splitOpen}
                    dietWarning={hasDietConflict(group, filters)} />
                ))}
              </div>
            </div>
          )}
        </section>
      );
    });
  };

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const expiringSoon = items.filter(i => {
    if (!i.expiry_date) return false;
    const days = Math.ceil((new Date(i.expiry_date) - new Date()) / 86400000);
    return days <= 3 && days >= 0;
  });

  return (
    <div className="flex flex-col h-screen" style={{ background: 'var(--bg-base)' }}>
      {ConfirmDialog}

      <div className="pt-safe flex-shrink-0" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ padding: '14px 16px 10px', position: 'relative' }}>
          <div style={{ paddingRight: 60 }}>
            <h1 className="ft-screen-title">{t(`nav.${listType}`)}</h1>
            <p className="ft-screen-subtitle">{t('storage.items', { count: totalQty })}</p>
          </div>
          <button onClick={() => setShowAdd(true)} className="ft-fab no-select"
            style={{ background: cfg.color, boxShadow: `0 4px 16px ${cfg.color.replace(')', ' / 35%)')}` }}>+</button>
        </div>

        {/* Storage unit pill-tabs */}
        {storageUnits.length > 1 && (
          <div style={{ display: 'flex', gap: 8, padding: '0 16px 10px', overflowX: 'auto' }}>
            <button onClick={() => setActiveUnit(null)}
              className={`ft-chip no-select ${!activeUnit ? 'active' : ''}`}
              style={!activeUnit ? { background: cfg.color, color: '#fff' } : {}}>
              {t('common.all')} {cfg.icon}
            </button>
            {storageUnits.map(unit => {
              const locked = unit.position > 0 && !isPremium;
              return (
                <button key={unit.id}
                  onClick={() => setActiveUnit(activeUnit === unit.id ? null : unit.id)}
                  className={`ft-chip no-select ${activeUnit === unit.id ? 'active' : ''}`}
                  style={activeUnit === unit.id ? { background: cfg.color, color: '#fff' }
                        : locked ? { opacity: 0.6 } : {}}>
                  {locked ? '🔒' : (unit.icon || cfg.icon)} {unit.name}
                </button>
              );
            })}
          </div>
        )}

        {/* View-toggle + søgebar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px 10px' }}>
          <div className="ft-search" style={{ margin: 0, flex: 1 }}>
            <svg className="ft-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input type="search" placeholder={`${t('common.search')}…`} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <ViewToggle viewMode={viewMode} setViewMode={handleSetViewMode} isPremium={isPremium} />
        </div>

        {expiringSoon.length > 0 && (
          <div style={{ margin: '0 16px 10px', background: 'oklch(55% 0.22 25 / 12%)', border: '1px solid oklch(55% 0.22 25 / 30%)', borderRadius: 12, padding: '8px 12px', fontSize: 12, color: 'var(--status-expired)', fontFamily: 'Manrope, sans-serif' }}>
            ⚠️ {t('storage.expiringSoon', { items: [...new Set(expiringSoon.map(i => i.name))].join(', ') })}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto pb-safe">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 64, color: 'var(--fg-4)', fontFamily: 'Manrope, sans-serif' }}>{t('common.loading')}</div>
        ) : filtered.length === 0 ? renderEmpty()
          : viewMode === 'image' ? renderImageGrid()
          : renderListItems()}
      </div>

      {showAdd && (
        <AddItemModal listType={listType} locations={locations} storageUnits={storageUnits}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}
      {selected && (
        <ItemDetailModal item={selected} listType={listType} locations={locations} storageUnits={allStorageUnits}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }}
          onDelete={() => { deleteItem(selected); setSelected(null); }} />
      )}
    </div>
  );
}
